import { Component, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { translate } from '@jsverse/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { PageChangeEvent } from '@progress/kendo-angular-grid';
import { first } from 'rxjs';
import { IBaseSearchRequest, IGridResult, IGridState, PageSize } from 'src/app/core/models/base';
import { enumToArray } from 'src/app/shared/helpers/enumToArray';
import { AppNotificationService } from 'src/app/shared/services/notification.service';
import { MustBeUniqueServiceValidator } from 'src/app/shared/validators-async/mustBeUniqueServices.validator';
import { ITimeEntryCategory } from 'src/app/time-tracking/models/time-entry-category';
import { TimeEntryCategoryService } from 'src/app/time-tracking/services/time-entry-category.service';
import { IService, ServiceCategories } from '../models/service';
import { ServiceService } from '../services/service.service';
import { TimeSpan } from 'src/app/shared/helpers/time-span';
import { flexibleTimeValidator } from 'src/app/shared/validators/timeStringValidator.validator';

@UntilDestroy()
@Component({
    selector: 'app-services-grid',
    templateUrl: './services-grid.component.html',
    styleUrls: ['./services-grid.component.css'],
    standalone: false
})
export class ServicesGridComponent implements OnInit {

  constructor(
    private serviceService: ServiceService,
    private route: ActivatedRoute,
    private timeEntryCategoryService: TimeEntryCategoryService,
    private appNotificationService: AppNotificationService
  ) { }

  services: IGridResult<IService[]>;
  public formGroup!: UntypedFormGroup;
  private editedRowIndex!: number;

  gridState: IGridState = {
    pageSize: 25,
    skip: 0,
  };

  searchRequest: IBaseSearchRequest = {
    pageNumber: 0,
    pageSize: 25,
    orderBy:['name']
  }

  get pageSizes() {
    return PageSize;
  }

  serviceCategories = enumToArray(ServiceCategories, "assignment.serviceCategories.")


  timeEntryCategories: ITimeEntryCategory[];

  noCategory = {
    id: null,
    name: "-"
  }

  get ServiceCategoriesEnum (){
    return ServiceCategories;
  }

  ngOnInit(): void {
    this.services = this.route.snapshot.data['services'] as IGridResult<IService[]>;
    this.timeEntryCategoryService.searchTimeEntryCategories({pageNumber: 1, pageSize: 999999})
      .pipe(untilDestroyed(this))
      .subscribe(response => this.timeEntryCategories = response.data);
  }

  updateList(){
    this.serviceService.searchServices(this.searchRequest)
    .pipe(
      first(),
      untilDestroyed(this))
    .subscribe(response => {
      const data = response.data;

      data.map(x => x.defaultTimeObj = TimeSpan.parse(x.defaultTime))
      this.services.data = data;
      this.services.total = response.totalCount;
    })
  }

  addHandler({ sender }){
    this.closeEditor(sender);

    this.formGroup = new UntypedFormGroup({
      name: new UntypedFormControl(null,
        {
          validators: Validators.required,
          asyncValidators: MustBeUniqueServiceValidator(this.serviceService, 'name'),
          updateOn:"blur"
      }),
      defaultTime: new UntypedFormControl(null, flexibleTimeValidator()),
      category: new UntypedFormControl(null),
      timeEntryCategoryId: new UntypedFormControl(null, Validators.required),
    });

    sender.addRow(this.formGroup);
  }

  editHandler({ sender, rowIndex, dataItem }){
    this.closeEditor(sender);

    this.formGroup = new UntypedFormGroup({
      name: new UntypedFormControl(dataItem.name,
        {
          validators: Validators.required,
          asyncValidators: MustBeUniqueServiceValidator(this.serviceService, 'name'),
          updateOn:"blur"
      }),
      defaultTime: new UntypedFormControl(dataItem.defaultTimeObj.toStringShort() ?? null, flexibleTimeValidator()),
      timeEntryCategoryId: new UntypedFormControl(dataItem.timeEntryCategoryId, Validators.required),
    });

    this.editedRowIndex = rowIndex;

    sender.editRow(rowIndex, this.formGroup);
  }

  public cancelHandler({ sender, rowIndex }) {
    this.closeEditor(sender, rowIndex);
  }

  public saveHandler(event) {
    const request: any = {
      name: event.formGroup.value.name,
      defaultTime: (event.formGroup.value.defaultTime != '  :  ' && event.formGroup.value.defaultTime != null) ? TimeSpan.parse(event.formGroup.value.defaultTime).toString() : null,
      timeEntryCategoryId: event.formGroup.value.timeEntryCategoryId,
      id: event.dataItem.id ?? null,
    }
    if (!event.dataItem.id){
      this.serviceService.createService(request)
      .subscribe(response => {
        this.updateList();
        this.appNotificationService.notification$.next({message: translate('assignment.service.createSuccess'), style: 'success'})
      });
    } else {
      this.serviceService.updateService(request)
      .subscribe(response => {
        this.updateList();
        this.appNotificationService.notification$.next({message: translate('assignment.service.saveSuccess'), style: 'success'})
      });;
    }

    event.sender.closeRow(event.rowIndex);
    
  }

  private closeEditor(grid, rowIndex = this.editedRowIndex) {
    grid.closeRow(rowIndex);
    this.editedRowIndex = undefined;
    this.formGroup = undefined;
  }

  deletionDialogOpened = false;
  private itemToDelete: { id: string } | null = null;

  public removeHandler({ dataItem }) {
    this.itemToDelete = dataItem;
    this.deletionDialogOpened = true;
  }

  public deleteConfirmed() {
    const dataItem = this.itemToDelete;
    this.itemToDelete = null;
    if (!dataItem) return;
    this.serviceService.deleteService(dataItem.id)
    .subscribe(response => {
      this.updateList();
      this.appNotificationService.notification$.next({message: translate('assignment.service.deleteSuccess'), style: 'success'})
    });;
  }

  onPageSizeChange(pageSize: number) {
    this.onPageChange({ skip: this.gridState.skip, take: pageSize });
  }

  onGridReload() {
    this.updateList();
  }

  onPageChange({ skip, take }: PageChangeEvent) {
    const page = Math.round((skip + take) / take);

    this.gridState.skip = skip;
    this.gridState.pageSize = take;

    this.searchRequest.pageNumber = page;
    this.searchRequest.pageSize = take;

    this.updateList();
  }

}
